import { memoize } from '@softgames/async-utils';
import { AdTimerEvent, GameEvent } from '@softgames/longplay-timer';
import type { App } from '@hexa/app/App';
import { getAdTimer } from '../helpers/getAdTimer';
import type { AzerionAds } from '../platform/AzerionAds';

/** AZ-998: active-gameplay time between long-play interstitials (150s or 180s). */
export const LONG_PLAY_INTERVAL_MS = 150_000;

/**
 * Final seconds handed to the package AdTimer (configured 6000/5000): its countdown tag shows
 * one tick in (5s left) and LongPlayTrigger fires at zero, so the warning is inside the interval.
 */
const WARNING_HANDOFF_MS = 6_000;

const TICK_MS = 1_000;

/**
 * AZ-998: the single automatic-ad timer for Azerion. Counts only active gameplay (Game scene,
 * level in play, no popup, tab visible, no ad running), preserves time across levels/pauses,
 * resets on any ad close, and lets a rewarded request cancel a pending long-play.
 */
export class LongPlayAdScheduler {
  private app: App | null = null;

  private appSignalsBound = false;

  private requestLongPlay: (() => void) | null = null;

  private isAdInFlight: (() => boolean) | null = null;

  private timeLeftMs = LONG_PLAY_INTERVAL_MS;

  private levelActive = false;

  private adShowing = false;

  private warningActive = false;

  private intervalId: number | null = null;

  constructor() {
    getAdTimer().on(AdTimerEvent.LongPlayTrigger, () => {
      this.onTrigger();
    });

    document.addEventListener('visibilitychange', () => {
      this.reevaluate();
    });
  }

  public attachApp(app: App): void {
    this.app = app;
  }

  public attachAds(ads: AzerionAds): void {
    this.requestLongPlay = (): void => {
      // eslint-disable-next-line no-void
      void ads.showInterstitialAd('longplay');
    };
    this.isAdInFlight = (): boolean => ads.isAdShowing;

    ads.onAdStarted.connect(() => {
      this.adShowing = true;
      this.cancelWarning();
    });

    ads.onAdFinished.connect(() => {
      this.adShowing = false;
      this.resetAfterAdClose();
    });

    this.startTicking();
  }

  public setLevelActive(active: boolean): void {
    this.levelActive = active;
    if (active) this.bindAppSignals();
    this.reevaluate();
  }

  /** Rewarded has priority: drop the pending long-play warning/request. */
  public cancelPendingLongPlay(): void {
    this.cancelWarning();
  }

  /** Any displayed ad closed: restart the full interval. */
  public resetAfterAdClose(): void {
    this.cancelWarning();
    this.timeLeftMs = LONG_PLAY_INTERVAL_MS;
  }

  private bindAppSignals(): void {
    if (this.appSignalsBound || !this.app) return;
    this.appSignalsBound = true;

    const { popups, sceneManager } = this.app.controller;
    popups.onPopupAdded.connect(() => {
      this.reevaluate();
    });
    popups.onPopupRemoved.connect(() => {
      this.reevaluate();
    });
    sceneManager.onSceneActivated.connect(() => {
      this.reevaluate();
    });
    sceneManager.onSceneDestroyed.connect(() => {
      this.reevaluate();
    });
  }

  private startTicking(): void {
    if (this.intervalId !== null) return;
    this.intervalId = window.setInterval(() => {
      this.tick();
    }, TICK_MS);
  }

  private isCountingAllowed(): boolean {
    if (!this.levelActive || this.adShowing || document.hidden) return false;
    if (!this.app) return false;

    const { popups, sceneManager } = this.app.controller;
    if (popups.stack.length > 0) return false;

    return sceneManager.activeScene?.id === 'Game';
  }

  private tick(): void {
    if (!this.isCountingAllowed()) return;
    // The package timer owns the final seconds.
    if (this.warningActive) return;

    this.timeLeftMs -= TICK_MS;

    if (this.timeLeftMs <= WARNING_HANDOFF_MS) {
      this.startWarning();
    }
  }

  private startWarning(): void {
    if (this.warningActive) return;
    this.warningActive = true;
    getAdTimer().emit(GameEvent.LevelStart);
  }

  private cancelWarning(): void {
    if (!this.warningActive) return;
    this.warningActive = false;
    getAdTimer().emit(GameEvent.LevelFinish);
    // Give the warning time back so it re-warns when gameplay resumes.
    this.timeLeftMs = WARNING_HANDOFF_MS;
  }

  private reevaluate(): void {
    if (this.warningActive && !this.isCountingAllowed()) {
      this.cancelWarning();
    }
  }

  private onTrigger(): void {
    this.warningActive = false;

    // Gameplay stopped or an ad is in flight: don't request, re-warn later.
    if (!this.isCountingAllowed() || (this.isAdInFlight !== null && this.isAdInFlight())) {
      this.timeLeftMs = WARNING_HANDOFF_MS;
      return;
    }

    this.timeLeftMs = LONG_PLAY_INTERVAL_MS;
    if (this.requestLongPlay !== null) this.requestLongPlay();
  }
}

export const getLongPlayAdScheduler = memoize(() => new LongPlayAdScheduler());
